import { withErrorBoundary } from "@sentry/nextjs";
import cn from "classnames";
import { gql } from "graphql-request";
import { useRouter } from "next/router";
import React, { VFC } from "react";
import styled from "styled-components";
import { Button } from "components";
import { useFormField } from "hooks";
import { FullArrow } from "icons";
import { regexp } from "lib/regexp";
import { getPipelineLink, getSource } from "lib/utils";
import { isBlockedPrefetchHref } from "lib/utils/isBlockedPrefetchUrl";
import { getPipelineLocalStorageData, storageService } from "lib/utils/localStorage";
import { CtaFormContent } from "types/graphql/generated";
interface Props {
  form: CtaFormContent & {
    prospectEventType?: string;
    sourceIdentifier?: string;
    planType?: string;
  };
  className?: string;
  isSecondary?: boolean;
}

const CtaForm: VFC<Props> = withErrorBoundary(
  ({
    form: { emailErrorMessage, emailLabel, emailLabelMobile, buttonLabel, buttonLink, sourceIdentifier },
    className = "",
    isSecondary = undefined,
  }) => {
    const { push, asPath } = useRouter();
    const source = getSource(asPath);

    const emailField = useFormField<HTMLInputElement>();

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();

      if (!emailField.value.match(regexp.email)) {
        emailField.setIsValid(false);

        return;
      }

      const data = {
        email: emailField.value,
        identifier: sourceIdentifier,
        source: sourceIdentifier ?? source,
      };

      const localStorageData = getPipelineLocalStorageData({ asPath, data, nextPipelinePath: buttonLink });

      await storageService.asyncSet(localStorageData.token, localStorageData);

      if (isBlockedPrefetchHref(buttonLink)) {
        // Do not prefetch external URLs
        window.location.assign(getPipelineLink(buttonLink, localStorageData));

        return;
      }

      push(buttonLink);
    };

    return (
      <StyledCtaForm onSubmit={handleSubmit} className={cn(className, {})}>
        <div className="CtaForm__formFieldsContainer">
          <div className="CtaForm__emailFieldContainer">
            <label
              htmlFor="ctaEmail"
              className={cn("CtaForm__emailLabel Color_secondary", {
                CtaForm__emailLabelFocus: emailField.isFocus,
                CtaForm__emailLabelNoVisible: !emailField.isFocus && emailField.value,
                CtaForm__emailLabelIsInvalid: !emailField.isValid,
              })}
            >
              {emailField.isValid ? emailLabel : emailErrorMessage}
            </label>
            <label
              htmlFor="ctaEmail"
              className={cn("CtaForm__emailLabelMobile Color_secondary", {
                CtaForm__emailLabelFocusMobile: emailField.isFocus,
                CtaForm__emailLabelNoVisibleMobile: !emailField.isFocus && emailField.value,
                CtaForm__emailLabelIsInvalidMobile: !emailField.isValid,
              })}
            >
              {emailField.isValid ? emailLabelMobile : emailErrorMessage}
            </label>
            <input
              id="ctaEmail"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              className={cn("CtaForm__email Font_text16 Color_black", {
                CtaForm__emailIsInvalid: !emailField.isValid,
              })}
              value={emailField.value}
              onChange={emailField.onChange}
              onFocus={emailField.onFocus}
              onBlur={emailField.onBlur}
            />
          </div>

          <Button
            type="submit"
            className="CtaForm__submitButton"
            {...(isSecondary ? { secondary: true } : { rightIcon: <FullArrow /> })}
            ariaLabel="submit"
          >
            {buttonLabel}
          </Button>
        </div>
      </StyledCtaForm>
    );
  },
  {}
);

const StyledCtaForm = styled.form`
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  max-width: 739px;
  box-shadow:
    0 8px 16px rgb(25 24 23 / 4%),
    0 12px 32px rgb(25 24 23 / 16%);
  border-radius: 8px;

  .CtaForm__formFieldsContainer {
    display: flex;
    justify-content: space-between;
    width: 100%;
    max-height: 64px;
  }

  .CtaForm__email {
    width: 100%;
    height: 100%;
    box-shadow: inset 0 0 0 1px #e2e6ec;
    border: none;
    border-radius: 8px 0 0 8px;
    background: ${({ theme }) => theme.colors.fog["00"]};
    padding: 0 24px;

    :focus {
      outline: none !important;
    }
  }

  .CtaForm__emailFieldContainer {
    position: relative;
    width: calc(100% - 16px);
    height: 64px;
  }

  .CtaForm__emailLabel {
    position: absolute;
    left: 0;
    top: 50%;
    transform: translate(0, -50%);
    padding-left: 24px;
    font-weight: 400;
    font-size: 18px;
    line-height: 26px;
    pointer-events: none;
    transition: 0.1s;
    padding-right: 12px;
  }

  .CtaForm__emailLabelMobile {
    display: none;
  }

  .CtaForm__emailLabelNoVisible {
    display: none;
  }

  .CtaForm__emailLabelFocus {
    font-size: 12px;
    display: none;
    border-radius: 2px;
    transform: translate(0, -60%);
    padding: 4px;
  }

  .CtaForm__emailLabelFocusMobile {
    display: none;
  }

  .CtaForm__emailLabelIsInvalid {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.fog["00"]};
    border-radius: 8px;
    left: 104px;
    top: 102px;
    transform: translate(0, -60%);
    display: block;
    padding: 10px 12px;
    animation: fade-in 200ms;
    background: ${({ theme }) => theme.colors.fog["70"]};
  }

  .CtaForm__emailLabelIsInvalidMobile {
    display: none;
  }

  .CtaForm__emailLabelIsInvalid::before {
    content: "";
    z-index: -1;
    position: absolute;
    width: 28px;
    height: 16px;
    top: 0;
    left: 72px;
    transform: rotate(45deg);
    border-radius: 2px;
    background-color: ${({ theme }) => theme.colors.fog["70"]};
    animation: fade-in 300ms;
  }

  @keyframes fade-in {
    0% {
      opacity: 0;
    }

    100% {
      opacity: 1;
    }
  }

  .CtaForm__client {
    padding: 0 40px 0 12px;
    appearance: none;
  }

  .CtaForm__clientOption {
    color: #000;
  }

  .CtaForm__clientColorTransparent {
    color: transparent;
  }

  .CtaForm__email:focus {
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.dusk["50"]};
    outline: none;
  }

  .CtaForm__emailIsInvalid,
  .CtaForm__emailIsInvalid:focus {
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.ember["30"]};
  }

  .CtaForm__submitButton {
    border: none;
    border-radius: 0 8px 8px 0;
    cursor: pointer;
    width: fit-content;
    white-space: nowrap;
  }

  .CtaForm__submitButtonIcon {
    width: 20px;
    height: 20px;
    fill: ${({ theme }) => theme.colors.fog["00"]};
  }

  @media (max-width: 660px) {
    .CtaForm__emailLabelIsInvalid,
    .CtaForm__emailLabelFocus,
    .CtaForm__emailLabelNoVisibleMobile,
    .CtaForm__emailLabel {
      display: none;
    }

    .CtaForm__emailLabelMobile {
      display: flex;
      position: absolute;
      left: 0;
      top: 50%;
      transform: translate(0, -50%);
      padding-left: 24px;
      font-weight: 400;
      font-size: 18px;
      line-height: 26px;
      pointer-events: none;
      transition: 0.1s;
    }

    .CtaForm__emailLabelFocusMobile {
      font-size: 12px;
      display: none;
      border-radius: 2px;
      transform: translate(0, -60%);
      padding: 4px;
    }

    .CtaForm__emailLabelIsInvalidMobile {
      font-size: 12px;
      color: ${({ theme }) => theme.colors.fog["00"]};
      border-radius: 8px;
      left: 20%;
      top: 102px;
      transform: translate(0, -60%);
      display: block;
      padding: 10px 12px;
      background: ${({ theme }) => theme.colors.fog["70"]};
    }

    .CtaForm__emailLabelIsInvalidMobile::before {
      content: "";
      z-index: -1;
      position: absolute;
      width: 28px;
      height: 16px;
      top: 0;
      left: 72px;
      transform: rotate(45deg);
      border-radius: 2px;
      background-color: ${({ theme }) => theme.colors.fog["70"]};
    }
  }

  @media (max-width: 480px) {
    .CtaForm__submitButton {
      width: fit-content;

      div {
        margin-left: 0;
      }

      span {
        display: none;
      }
    }
  }
`;

gql`
  fragment CtaFormContent on ComponentNestedCta3Form {
    buttonLabel
    buttonLink
    emailErrorMessage
    emailLabel
    emailLabelMobile
  }
`;

export { CtaForm };
